import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Row, Col, Input } from "reactstrap";
import Select from "react-select";
import TableSearch from "../../../Components/Common/TableSearch";
import VoucherService from "../../../services/AccountingServices/VoucherService";
import FiscalYearService from "../../../services/AccountingServices/FiscalYearService";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import useServerSideSearch from "../../../hooks/useServerSideSearch";
import VoucherForm from "./VoucherForm";
import VoucherDetail from "./VoucherDetail";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";

const Vouchers = () => {
  const navigate = useNavigate();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    FiscalYearCode: null,
    VoucherType: null,
    Status: null,
  });

  const {
    data: vouchers,
    loading,
    totalRows,
    currentPage,
    pageSize,
    searchQuery,
    handleSearch,
    handleFilterChange,
    handlePaginationChange,
    refetch,
  } = useServerSideSearch(VoucherService.getList);

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  const fetchFiscalYears = async () => {
    try {
      const response = await FiscalYearService.getList();
      const items = Array.isArray(response) ? response : response?.items || [];
      // Only show active fiscal years in the selection filter
      const formatted = items
        .filter((fy) => fy.isActive)
        .map((fy) => ({ value: fy.code, label: fy.name }));
      setFiscalYears(formatted);
    } catch (error) {
      console.error("Error fetching fiscal years:", error);
    }
  };

  const voucherTypeOptions = [
    { value: "JOURNAL", label: "JOURNAL" },
    { value: "INCOME", label: "INCOME" },
    { value: "PAYMENT", label: "PAYMENT" },
  ];

  const statusOptions = [
    { value: "NEW", label: "New" },
    { value: "INITIATED", label: "Initiated" },
    { value: "REVIEWED", label: "Reviewed" },
    { value: "APPROVED", label: "Approved" },
    { value: "REJECTED", label: "Rejected" },
  ];

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const toggleForm = () => {
    if (isFormOpen) setSelectedItem(null);
    setIsFormOpen(!isFormOpen);
  };

  const toggleDelete = () => setIsDeleteOpen(!isDeleteOpen);

  const handleAdd = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleView = (item) => {
    navigate(`/vouchers/${item.id}`);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await VoucherService.delete(selectedItem.id);
      toast.success("Voucher deleted successfully");
      refetch();
      toggleDelete();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete voucher");
    }
  };

  const handleApplyFilters = (name, selectedOption) => {
    const value = selectedOption?.value || null;
    const newFilters = { ...selectedFilters, [name]: value };
    setSelectedFilters(newFilters);
    handleFilterChange(newFilters);
  };

  const getStatusBadge = (status) => {
    const colors = {
      NEW: "primary",
      INITIATED: "info",
      REVIEWED: "warning",
      APPROVED: "success",
      REJECTED: "danger",
    };
    return (
      <div className="text-center" style={{ width: "120px" }}>
        <Badge 
          color={colors[status] || "secondary"} 
          className="text-uppercase py-1 fs-10 text-center"
         
        >
          {status}
        </Badge>
      </div>
    );
  };

 const columns = [
  {
    name: "S.N",
    selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
    width: "60px",
    center: true,
    cell: (row, index) => (
      <span className="text-muted small">
        {(currentPage - 1) * pageSize + index + 1}
      </span>
    ),
  },
  {
    name: "Voucher SN",
    selector: (row) => row.voucherSn,
    sortable: true,
    // grow: 1.2,
    cell: (row) => (
      <div className="fw-bold text-primary" style={{ paddingLeft: "4px" }}>
        {row.voucherSn}
      </div>
    ),
  },
  {
    name: "Date",
    selector: (row) => row.valueDate,
    sortable: true,
    width: "115px",
    center: true,
    cell: (row) => (
      <span className="text-muted small">
        <i className="ri-calendar-line me-1"></i>
        {new Date(row.valueDate).toLocaleDateString()}
      </span>
    ),
  },
  {
    name: "Type",
    selector: (row) => row.voucherType,
    sortable: true,
    width: "115px",
    center: true,
    cell: (row) => {
      const typeColors = { JOURNAL: "primary", INCOME: "success", PAYMENT: "warning" };
      return (
        <Badge
          color={typeColors[row.voucherType] || "info"}
          className="text-uppercase px-2 py-1 fw-bold fs-10"
          style={{ minWidth: "75px", textAlign: "center", display: "inline-block" }}
        >
          {row.voucherType}
        </Badge>
      );
    },
  },
  {
    name: "Fiscal Year",
    selector: (row) => row.fiscalYearCode,
    width: "115px",
    center: true,
    cell: (row) => (
      <Badge
        color="soft-dark"
        className="text-muted"
        style={{ minWidth: "65px", textAlign: "center", display: "inline-block" }}
      >
        {row.fiscalYearCode}
      </Badge>
    ),
  },
  {
    name: "Description",
    selector: (row) => row.description,
    wrap: true,
    grow: 1.3,   // reduced from 2 → 1.3
    cell: (row) => (
      <div
        className="text-muted small py-2"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          whiteSpace: "normal",
          lineHeight: "1.5",
          paddingLeft: "4px",
        }}
      >
        {row.description}
      </div>
    ),
  },
  {
    name: "Status",
    selector: (row) => row.status,
    sortable: true,
    // width: "110px",
    center: true,
    cell: (row) => getStatusBadge(row.status),
  },
];

  const stats = [
    { title: "Total Vouchers", value: totalRows, color: "#405189", icon: "ri-file-list-3-line" },
    { title: "Pending Flow", value: vouchers.filter(v => ["NEW", "INITIATED", "REVIEWED"].includes(v.status)).length, color: "#f7b84b", icon: "ri-time-line" },
    { title: "Approved Doc", value: vouchers.filter(v => v.status === "APPROVED").length, color: "#0ab39c", icon: "ri-checkbox-circle-line" },
    { title: "Rejected Doc", value: vouchers.filter(v => v.status === "REJECTED").length, color: "#f06548", icon: "ri-close-circle-line" },
  ];

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "8px",
      borderColor: "#dee2e6",
      "&:hover": { borderColor: "#86b7fe" },
      fontSize: "0.85rem"
    }),
    option: (base) => ({
      ...base,
      fontSize: "0.85rem"
    })
  };

  return (
    <div className="page-content p-0">
      <ResponsiveDataManagement
        title="Vouchers"
        pageTitle="Accounting"
        mobileCardTitle={null}
        data={vouchers}
        columns={columns}
        stats={stats}
        searchKeys={["voucherSn", "description"]}
        onAdd={null}
        onEdit={(row) => row.status === "NEW" ? handleEdit(row) : toast.error("Only NEW vouchers can be edited")}
        onView={handleView}
        onDelete={handleDeleteConfirm}
        loading={loading}
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onPaginationChange={handlePaginationChange}
        onSearch={handleSearch}
        searchTerm={searchQuery}
        showSearch={false}
        emptyStateMessage="No vouchers found matching your criteria"
        customHeaderContent={
          <Row className="mb-3 align-items-center">
            <Col lg={3}>
              <h5 className="mb-1 fw-bold text-dark">Voucher List</h5>
              <p className="text-muted small mb-0">Accounting journal history</p>
            </Col>
            <Col md={9}> 
              <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap flex-lg-nowrap">
                <div style={{ flex: '1 1 auto', minWidth: '150px', maxWidth: '250px' }}>
                  <TableSearch
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search vouchers..."
                  />
                </div>
                
                <div style={{ minWidth: "140px" }}>
                  <Select
                    options={fiscalYears}
                    value={fiscalYears.find(opt => opt.value === selectedFilters.FiscalYearCode)}
                    onChange={(opt) => handleApplyFilters("FiscalYearCode", opt)}
                    placeholder="Fiscal Year"
                    isClearable={true}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div style={{ minWidth: "130px" }}>
                  <Select
                    options={voucherTypeOptions}
                    value={voucherTypeOptions.find(opt => opt.value === selectedFilters.VoucherType)}
                    onChange={(opt) => handleApplyFilters("VoucherType", opt)}
                    placeholder="All Types"
                    isClearable={true}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div style={{ minWidth: "140px" }}>
                  <Select
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === selectedFilters.Status)}
                    onChange={(opt) => handleApplyFilters("Status", opt)}
                    placeholder="All Status"
                    isClearable={true}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                <Button
                  color="success"
                  onClick={handleAdd}
                  className="d-flex align-items-center gap-1"
                  style={{ whiteSpace: "nowrap", height: "38px" }}
                >
                  <i className="bx bx-plus fs-16"></i> Add Voucher
                </Button>
              </div>
            </Col>
          </Row>
        }
        mobileCardRenderer={(item) => {
          const colors = {
            NEW: "primary",
            INITIATED: "info",
            REVIEWED: "warning",
            APPROVED: "success",
            REJECTED: "danger",
          };
          return (
            <div key={item.id} className="card shadow-sm border-0 mb-3 overflow-hidden">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h6 className="fw-bold text-primary mb-1">{item.voucherSn}</h6>
                    <div className="text-muted small">
                      <i className="ri-calendar-line me-1"></i>
                      {new Date(item.valueDate).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge 
                    color={colors[item.status] || "secondary"} 
                    className="text-uppercase py-1 fs-10"
                    style={{ minWidth: "80px", textAlign: "center" }}
                  >
                    {item.status}
                  </Badge>
                </div>
                <div className="mb-2">
                  <Badge color="soft-info" className="text-uppercase px-2 py-1 fw-bold fs-10">{item.voucherType}</Badge>
                  <span className="text-muted small ms-2">FY: {item.fiscalYearCode}</span>
                </div>
                <p className="text-muted small mb-3 text-truncate-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {item.description}
                </p>
                <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                  <div className="text-success fw-bold small">
                    {item.totalDr.toLocaleString(undefined, { minimumFractionDigits: 2 })} DR
                  </div>
                  <div className="d-flex gap-2">
                    <Button color="soft-info" size="sm" onClick={() => handleView(item)}>
                      <i className="ri-eye-line"></i>
                    </Button>
                    {item.status === "NEW" && (
                      <Button color="soft-warning" size="sm" onClick={() => handleEdit(item)}>
                        <i className="ri-edit-line"></i>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />

      <VoucherForm
        isOpen={isFormOpen}
        toggle={toggleForm}
        selectedItem={selectedItem}
        refetch={refetch}
      />

      <DeleteModal
        show={isDeleteOpen}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDeleteConfirm}
        itemName={selectedItem?.voucherSn}
      />
    </div>
  );
};

export default Vouchers;
